import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Field, Input, Select } from "../ui";
import { FIELDS, FLAG_FIELDS, TEXT_FIELDS, newRuleId, type Actions, type Condition, type Rule } from "./api";

const list = (v: string) =>
  v
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

function ConditionRow({
  cond,
  onChange,
  onRemove,
}: {
  cond: Condition;
  onChange(c: Condition): void;
  onRemove(): void;
}) {
  const { t } = useTranslation();
  const text = TEXT_FIELDS.includes(cond.field);
  const flag = FLAG_FIELDS.includes(cond.field);
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="cond-row">
      <Select
        compact
        value={cond.field}
        aria-label={t("mail.cond.field")}
        onChange={(e) => onChange({ field: e.target.value, op: "contains", value: "" })}
        data-testid="cond-field"
      >
        {FIELDS.map((f) => (
          <option key={f} value={f}>
            {t(`mail.field.${f}`)}
          </option>
        ))}
      </Select>
      {text && (
        <Select
          compact
          value={cond.op ?? "contains"}
          aria-label={t("mail.cond.op")}
          onChange={(e) => onChange({ ...cond, op: e.target.value as Condition["op"] })}
          data-testid="cond-op"
        >
          {(["contains", "is", "matches"] as const).map((o) => (
            <option key={o} value={o}>
              {t(`mail.op.${o}`)}
            </option>
          ))}
        </Select>
      )}
      {cond.field === "attachmentType" && (
        <Select
          compact
          value={cond.value || "pdf"}
          aria-label={t("mail.cond.value")}
          onChange={(e) => onChange({ ...cond, op: "is", value: e.target.value })}
        >
          {["pdf", "image", "calendar"].map((k) => (
            <option key={k} value={k}>
              {t(`mail.attachmentType.${k}`)}
            </option>
          ))}
        </Select>
      )}
      {!flag && cond.field !== "attachmentType" && (
        <Input
          className="h-[30px] w-48 text-[13px]"
          value={cond.value ?? ""}
          aria-label={t("mail.cond.value")}
          placeholder={t(`mail.placeholder.${cond.field}`, { defaultValue: "" })}
          onChange={(e) => onChange({ ...cond, value: e.target.value })}
          data-testid="cond-value"
        />
      )}
      <button
        type="button"
        className="grid h-7 w-7 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        aria-label={t("mail.cond.remove")}
        onClick={onRemove}
      >
        <Trash2 size={14} aria-hidden />
      </button>
    </div>
  );
}

function RuleCard({
  rule,
  index,
  count,
  workspaces,
  people,
  onChange,
  onMove,
  onRemove,
}: {
  rule: Rule;
  index: number;
  count: number;
  workspaces: { id: string; name: string }[];
  people: { id: string; label: string }[];
  onChange(r: Rule): void;
  onMove(delta: number): void;
  onRemove(): void;
}) {
  const { t } = useTranslation();
  const then = rule.then;
  const set = (patch: Partial<Actions>) => {
    const next: Actions = { ...then, ...patch };
    for (const k of Object.keys(next) as (keyof Actions)[]) {
      const v = next[k];
      if (v === undefined || v === "" || v === false || (Array.isArray(v) && v.length === 0)) delete next[k];
    }
    onChange({ ...rule, then: next });
  };
  const conds = rule.when;
  return (
    <li
      className="flex flex-col gap-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800"
      data-testid="rule-row"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11.5px] text-neutral-500">{index + 1}</span>
        <Input
          className="h-[30px] w-56 text-[13px]"
          value={rule.name}
          placeholder={t("mail.rule.namePlaceholder")}
          aria-label={t("mail.rule.name")}
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
          data-testid="rule-name"
        />
        <label className="flex items-center gap-1.5 text-[12.5px]">
          <Checkbox
            checked={!rule.off}
            label={t("mail.rule.on")}
            onChange={(v) => onChange({ ...rule, off: !v || undefined })}
          />
          {t("mail.rule.on")}
        </label>
        <span className="ml-auto flex gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label={t("mail.rule.up")}
          >
            <ArrowUp size={14} aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
            aria-label={t("mail.rule.down")}
          >
            <ArrowDown size={14} aria-hidden />
          </Button>
          <Button size="sm" variant="secondary" onClick={onRemove} aria-label={t("mail.rule.remove")}>
            <Trash2 size={14} aria-hidden />
          </Button>
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] text-neutral-500">
          {conds.length ? t("mail.rule.when") : t("mail.rule.always")}
        </span>
        {conds.map((c, i) => (
          <ConditionRow
            key={i}
            cond={c}
            onChange={(next) => onChange({ ...rule, when: conds.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => onChange({ ...rule, when: conds.filter((_, j) => j !== i) })}
          />
        ))}
        <Button
          size="sm"
          variant="secondary"
          className="w-fit"
          onClick={() =>
            onChange({ ...rule, when: [...conds, { field: "subject", op: "contains", value: "" }] })
          }
          data-testid="cond-add"
        >
          {t("mail.cond.add")}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[12px] text-neutral-500">{t("mail.rule.then")}</span>
        <label className="flex items-center gap-2 text-[13px]">
          <Checkbox
            checked={Boolean(then.ignore)}
            label={t("mail.action.ignore")}
            onChange={(v) => set({ ignore: v })}
          />
          {t("mail.action.ignore")}
        </label>
        {!then.ignore && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label={t("mail.action.workspace")}>
              <Select
                compact
                value={then.workspace ?? ""}
                onChange={(e) => set({ workspace: e.target.value || undefined })}
              >
                <option value="">{t("mail.action.accountDefault")}</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("mail.action.to")}>
              <Select
                compact
                value={then.to ?? ""}
                onChange={(e) => set({ to: (e.target.value || undefined) as Actions["to"] })}
                data-testid="rule-to"
              >
                <option value="">{t("mail.action.accountDefault")}</option>
                <option value="task">{t("mail.to.task")}</option>
                <option value="tray">{t("mail.to.tray")}</option>
              </Select>
            </Field>
            <Field label={t("mail.action.group")}>
              <Input
                className="h-[30px] text-[13px]"
                value={then.group ?? ""}
                onChange={(e) => set({ group: e.target.value })}
                data-testid="rule-group"
              />
            </Field>
            <Field label={t("mail.action.tags")}>
              <Input
                className="h-[30px] text-[13px]"
                defaultValue={(then.tags ?? []).join(", ")}
                onBlur={(e) => set({ tags: list(e.target.value) })}
                data-testid="rule-tags"
              />
            </Field>
            <Field label={t("mail.action.contexts")}>
              <Input
                className="h-[30px] text-[13px]"
                defaultValue={(then.contexts ?? []).join(", ")}
                placeholder="@errand"
                onBlur={(e) => set({ contexts: list(e.target.value) })}
              />
            </Field>
            <Field label={t("mail.action.priority")}>
              <Select
                compact
                value={then.priority ?? ""}
                onChange={(e) => set({ priority: (e.target.value || undefined) as Actions["priority"] })}
              >
                <option value="">—</option>
                {(["high", "normal", "low"] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`mail.priority.${p}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("mail.action.energy")}>
              <Select
                compact
                value={then.energy ?? ""}
                onChange={(e) => set({ energy: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">—</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("mail.action.estimate")}>
              <Input
                className="h-[30px] text-[13px]"
                inputMode="numeric"
                defaultValue={then.estimateMin ?? ""}
                onBlur={(e) =>
                  set({ estimateMin: Number(e.target.value) > 0 ? Number(e.target.value) : undefined })
                }
              />
            </Field>
            <Field label={t("mail.action.attachments")}>
              <Select
                compact
                value={then.attachments ?? "keep"}
                onChange={(e) => set({ attachments: e.target.value === "drop" ? "drop" : undefined })}
              >
                <option value="keep">{t("mail.attachments.keep")}</option>
                <option value="drop">{t("mail.attachments.drop")}</option>
              </Select>
            </Field>
          </div>
        )}
        {!then.ignore && people.length > 0 && (
          <fieldset className="flex flex-wrap gap-x-4 gap-y-1">
            <legend className="mb-1 text-[12px] text-neutral-500">{t("mail.action.with")}</legend>
            {people.map((p) => (
              <label key={p.id} className="flex items-center gap-1.5 text-[13px]">
                <Checkbox
                  checked={(then.with ?? []).includes(p.id)}
                  label={p.label}
                  onChange={(v) =>
                    set({
                      with: v ? [...(then.with ?? []), p.id] : (then.with ?? []).filter((x) => x !== p.id),
                    })
                  }
                />
                {p.label}
              </label>
            ))}
          </fieldset>
        )}
        <label className="flex items-center gap-2 text-[13px]">
          <Checkbox
            checked={Boolean(then.anySender)}
            label={t("mail.action.anySender")}
            onChange={(v) => set({ anySender: v })}
          />
          {t("mail.action.anySender")}
        </label>
      </div>
    </li>
  );
}

/** The routing rules of one account, in order (I6). */
export function RulesEditor({
  rules,
  onChange,
  workspaces,
  people,
}: {
  rules: Rule[];
  onChange(next: Rule[]): void;
  workspaces: { id: string; name: string }[];
  people: { id: string; label: string }[];
}) {
  const { t } = useTranslation();
  const move = (i: number, d: number) => {
    const next = [...rules];
    const [r] = next.splice(i, 1);
    next.splice(i + d, 0, r);
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {rules.map((r, i) => (
          <RuleCard
            key={r.id}
            rule={r}
            index={i}
            count={rules.length}
            workspaces={workspaces}
            people={people}
            onChange={(next) => onChange(rules.map((x, j) => (j === i ? next : x)))}
            onMove={(d) => move(i, d)}
            onRemove={() => onChange(rules.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
      <Button
        size="sm"
        variant="secondary"
        className="w-fit"
        onClick={() => onChange([...rules, { id: newRuleId(), name: "", when: [], then: {} }])}
        data-testid="rule-add"
      >
        {t("mail.rule.add")}
      </Button>
    </div>
  );
}
